import { dateFieldFilterComparator, caseInsensitiveComparator, alphaNumericComparator, dateFieldComparator } from '@/modules/base/components/list/comparator'
import { ListDefaultSettings } from '@/modules/base/components/list/constants'
import moment from 'moment'
import config from '@/config'
import { formatCurrency } from '@/modules/_helpers/formatCurrency'
import { convertTagsToFlattenObjectArray, tagsValueGetter } from '@/modules/_helpers/convertTagsToFlattenObjectArray'

const CUSTOM_CLASS_FIRST = 'ag-custom-first'
const CUSTOM_CLASS_REVERSED = 'ag-custom-reversed'
const DATE_FORMAT = config.dateFormat.client
const DATETIME_FORMAT = config.dateFormat.dateTime

export interface ColumnApi {
  getAllGridColumns(): Array<{ colId: string }>
}

export interface GridParams {
  columnApi: ColumnApi
}

export interface ListColumnModelData {
  name?: string
  label?: string
  subtitle?: string
  required?: boolean
  active?: boolean
  default?: boolean
  type?: 'date' | 'number' | 'currency' | 'text' | 'datetime' | 'tag'
  sort?: boolean
  width?: number
  headerCheckboxSelection?: boolean
  headerCheckboxSelectionFilteredOnly?: boolean
  checkboxSelection?: boolean
  headerClass?: string | ((params: GridParams) => string)
  cellClass?: string | ((params: GridParams) => string)
}

export interface FilterParamOptions {
  isOptional?: boolean
  inRangeInclusive?: boolean
  filterOptions?: (string | { displayKey: string; displayName: string; test: (filterValue: unknown, cellValue: unknown) => boolean })[]
  comparator?: (valueA: unknown, valueB: unknown) => number
  [key: string]: unknown
}

export interface CellRendererParams {
  value: unknown
  columnApi: ColumnApi
}

export interface QuickFilterParams {
  value: unknown
}

export default class ListColumnModel {
  // Custom props
  private _reversed: boolean = false
  public name: string = ''
  public label: string = ''
  public subtitle: string = ''
  public required?: boolean
  public active?: boolean
  public default?: boolean
  public customType?: string

  // ag-grid props
  public field: string = ''
  public headerName: string = ''
  public sort: boolean = false
  public width: number = 100
  public hide: boolean = false
  public headerCheckboxSelection: boolean = false
  public headerCheckboxSelectionFilteredOnly: boolean = false
  public checkboxSelection: boolean = false
  public headerClass?: string | ((params: GridParams) => string)
  public cellClass?: string | ((params: GridParams) => string)

  // Static properties
  public minWidth: number = 100
  public lockPinned: boolean = true
  public menu: string = ''

  // ag-grid additional properties
  public sortable?: boolean
  public filter?: string | boolean
  public cellRenderer?: string
  public comparator?: (valueA: unknown, valueB: unknown) => number
  public filterParams?: {
    filterOptions?: (string | { displayKey: string; displayName: string; test: (filterValue: unknown, cellValue: unknown) => boolean })[]
    [key: string]: unknown
  }
  public getQuickFilterText?: (params: QuickFilterParams) => string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public valueGetter?: (params: any) => unknown
  public menuTabs?: string[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public valueFormatter?: (params: any) => unknown

  constructor(data: ListColumnModelData) {
    // Custom props
    this._reversed = false
    this.name = data.name || ''
    this.label = data.label || ''
    this.subtitle = data.subtitle || ''
    this.required = data.required
    this.active = data.active
    this.default = data.default
    this.customType = data.type

    // ag-grid props
    this.field = data.name || ''
    this.headerName = this.label || ''
    this.sort = data.sort || false
    this.width = data.width || 100
    this.hide = !data.active
    this.headerCheckboxSelection = data.headerCheckboxSelection || false
    this.headerCheckboxSelectionFilteredOnly = data.headerCheckboxSelectionFilteredOnly || false
    this.checkboxSelection = data.checkboxSelection || false

    this.headerClass = data.headerClass
    this.cellClass = data.cellClass

    // Static properties
    this.minWidth = 100
    this.lockPinned = true
    this.menu = ''

    // Callbacks called on grid rendering to attach `.first` css class to currently first column
    if (!this.cellClass) {
      this.cellClass = (params: GridParams) => this.getColumnClass(params.columnApi)
    }
    if (!this.headerClass) {
      this.headerClass = (params: GridParams) => this.getColumnClass(params.columnApi)
    }

    switch (this.customType) {
      case 'date': {
        this.setDateType({ inRangeInclusive: true })
        break
      }
      case 'number': {
        this.setNumberType()
        break
      }
      case 'currency': {
        this.setCurrencyType({
          inRangeInclusive: true,
        })
        break
      }
      default:
        this.setTextType({ inRangeInclusive: true })
        break
    }
  }

  getColumnClass(columnApi: ColumnApi): string {
    const columns = columnApi.getAllGridColumns()
    const classes: string[] = []

    if (columns[0].colId === this.name) {
      classes.push(CUSTOM_CLASS_FIRST)
    }

    if (this._reversed) {
      classes.push(CUSTOM_CLASS_REVERSED)
    }

    return classes.join(' ')
  }

  setTextType({ isOptional = false, inRangeInclusive = false, ...options }: FilterParamOptions = {}): void {
    this.sortable = true
    this.comparator = caseInsensitiveComparator
    this.setFilterParam({
      isOptional,
      inRangeInclusive,
      filterOptions: ListDefaultSettings.TEXT_FILTER_OPTIONS,
      ...options,
    })
  }

  setNumberType({ isOptional = false, inRangeInclusive = false, ...options }: FilterParamOptions = {}): void {
    this.sortable = true
    this.filter = 'agNumberColumnFilter'
    this.comparator = alphaNumericComparator
    this.setFilterParam({
      isOptional,
      inRangeInclusive,
      filterOptions: ListDefaultSettings.NUMBER_FILTER_OPTIONS,
      ...options,
    })
  }

  setDateType({ isOptional = false, inRangeInclusive = true, ...options }: FilterParamOptions = {}): void {
    this.sortable = true
    this.filter = 'agDateColumnFilter'
    this.cellRenderer = 'dateCellRenderer'
    this.comparator = dateFieldComparator
    this.setFilterParam({
      isOptional,
      inRangeInclusive,
      comparator: dateFieldFilterComparator,
      filterOptions: ListDefaultSettings.DATE_FILTER_OPTIONS,
      ...options,
    })
    this.getQuickFilterText = ({ value }: QuickFilterParams) => value ? moment(value).format(DATE_FORMAT) : ''
  }

  setDateTimeType({ isOptional = false, inRangeInclusive = true, ...options }: FilterParamOptions = {}): void {
    this.sortable = true
    this.filter = 'agDateColumnFilter'
    this.cellRenderer = 'dateTimeCellRenderer'
    this.comparator = dateFieldComparator
    this.setFilterParam({
      isOptional,
      inRangeInclusive,
      comparator: dateFieldFilterComparator,
      filterOptions: ListDefaultSettings.DATE_FILTER_OPTIONS,
      ...options,
    })
    this.getQuickFilterText = ({ value }: QuickFilterParams) => value ? moment(value).format(DATETIME_FORMAT) : ''
  }

  setCurrencyType({ isOptional = false, inRangeInclusive = true, ...options }: FilterParamOptions = {}): void {
    this.sortable = true
    this.cellRenderer = 'currencyAmountCellRenderer'
    this.filter = 'agNumberColumnFilter'
    this.setFilterParam({
      isOptional,
      inRangeInclusive,
      filterOptions: ListDefaultSettings.NUMBER_FILTER_OPTIONS,
      ...options,
    })
    this.getQuickFilterText = (params: QuickFilterParams): string => {
      return formatCurrency(params.value)
    }
  }

  setTagType({ isOptional = false, inRangeInclusive = false, ...options }: FilterParamOptions = {}): void {
    this.sort = false
    this.sortable = false
    this.cellRenderer = 'tagCellRenderer'
    this.filter = 'listTagFilter'
    this.menuTabs = ['filterMenuTab']
    this.getQuickFilterText = convertTagsToFlattenObjectArray
    this.valueGetter = tagsValueGetter
    this.setFilterParam({
      isOptional,
      inRangeInclusive,
      ...options,
    })
  }

  setFilterParam({ isOptional = false, ...options }: FilterParamOptions): void {
    this.filterParams = {
      filterOptions: [],
      ...this.filterParams,
      ...options,
    }

    const blankOptions = ListDefaultSettings.BLANK_OPTIONS_FILTER_OPTIONS as string[]
    const currentFilterOptions = this.filterParams.filterOptions || []

    if (isOptional && !currentFilterOptions.includes(blankOptions[0])) {
      this.filterParams.filterOptions = [...currentFilterOptions, ...blankOptions]
    }
    if (!isOptional && currentFilterOptions.includes(blankOptions[0])) {
      this.filterParams.filterOptions = currentFilterOptions.filter((item): item is string => typeof item === 'string' && !blankOptions.includes(item))
    }
  }
}
